import Vue from 'vue';
import { Component } from 'vue-property-decorator';
import VueSelect from 'vue-select'; // needed for multiselect not ported yet
import 'vue-select/dist/vue-select.css'; // import css
import Toasted from 'vue-toasted';
import Datepicker from 'vuejs-datepicker';
import { handleError, initClient } from '../../api';
import { fixISettings } from '../../util';
import { ChartwizardApiClient, ChartwizardTemplate, ChartwizardTemplateGraph, ChartwizardTemplates, ChartwizardTranslation, ChartwizardTranslationClient, ChartwizardUnit, ChartwizardUnitClient, DataPoint, Selection, SettingsApiControllerUIClient, Table } from '../backend/backendapi';
import CreateTemplateModal from './createTemplateModal/createTemplateModal.controller';
import LineChartComponent from './linechart.controller';
import { Selectable, Series } from './linechart.model';

Vue.use(Toasted, { duration: 2000 });

@Component({
    components: {
        linechart: require("./linechart.vue.html").default,
        CreateTemplateModal: require("./createTemplateModal/createTemplateModal.vue.html").default,
        SelectTemplateModal: require("./selectTemplateModal/selectTemplateModal.vue.html").default,
        Datepicker,
        VueSelect,
    }
})
export default class LCContainerComponent extends Vue {
    //data
    private readonly dbtables: Table[] = [];
    private readonly translations: ChartwizardTranslation[] = [];
    private readonly units: ChartwizardUnit[] = [];
    private templates: ChartwizardTemplates | null = null;

    //ui selection stuff
    private selectedServer: Selectable | null = null;
    private selectedTable: Selectable | null = null;
    private selectedField: Selectable | null = null;
    private from: Date = new Date();
    private to: Date = new Date();

    //apis
    private translationAPI = initClient(ChartwizardTranslationClient);
    private unitAPI = initClient(ChartwizardUnitClient);
    private chartwizardAPI = initClient(ChartwizardApiClient);
    private settingsAPI = initClient(SettingsApiControllerUIClient);

    constructor() {
        super();
        this.from.setDate(this.from.getDate() - 1);
        this.to.setDate(this.to.getDate() + 1);
        fixISettings();
    }

    mounted() {
        this.reloadTables();
        this.reloadTranslations();
        this.reloadUnits();
        this.reloadTemplates();
    }

    private async reloadTables(): Promise<void> {
        const tables = (await this.chartwizardAPI.getTables()).result!;
        this.dbtables.splice(0);
        this.dbtables.push(...tables);
    }

    private async reloadTranslations(): Promise<void> {
        const translations = (await this.translationAPI.list()).result!;
        this.translations.splice(0);
        this.translations.push(...translations);
    }

    private async reloadUnits(): Promise<void> {
        const units = (await this.unitAPI.list()).result!;
        this.units.splice(0);
        this.units.push(...units);
    }

    private async reloadTemplates(): Promise<void> {
        const set = (await this.settingsAPI.get('ChartwizardTemplates', false, false)).result!;
        this.templates = new ChartwizardTemplates();
        this.templates.init(set);
    }

    private addLineToChart(): void {
        this.fetchAndDrawDataPoints();
    }

    private reloadDataChart(): void {
        var sels = (this.$refs.lc as LineChartComponent).chartData.map(series => {
            var sel = new Selection();
            sel.server = series.server;
            sel.dbase = series.dbase;
            sel.table = series.table;
            sel.field = series.field;
            sel.start = series.start;
            sel.end = series.end;
            return sel;
        });
        this.resetChart();
        sels.forEach(sel => this.loadSelection(sel));
    }

    private resetChart(): void {
        (this.$refs.lc as LineChartComponent).reset();
    }

    private async fetchAndDrawDataPoints(): Promise<void> {
        return this.loadSelection(this.selection);
    }

    private async loadSelection(sel: Selection, color: string = this.getRandomColor()): Promise<void> {
        const data = (await this.chartwizardAPI.getData(sel)).result!;
        if (data.length == 0) {
            this.$toasted.info('No data found!');
            return;
        }
        var suffix = '_' + sel.dbase + "_" + sel.table + "_" + sel.field + '_' + new Date().getTime();

        var series = new Series();
        series.data = data as DataPoint[];
        series.server = sel.server;
        series.dbase = sel.dbase;
        series.table = sel.table;
        series.field = sel.field;
        series.start = sel.start;
        series.end = sel.end;
        series.xpropname = 'date' + suffix;
        series.ypropname = 'value' + suffix;
        series.color = color;
        series.label = (this.translationMap.get('server.' + sel.server) ?? sel.server)
            + '.' + (this.translationMap.get('database.' + sel.server + '.' + sel.dbase) ?? sel.dbase)
            + '.' + (this.translationMap.get('table.' + sel.server + '.' + sel.dbase + '.' + sel.table) ?? sel.table)
            + '.' + (this.translationMap.get('field.' + sel.server + '.' + sel.dbase + '.' + sel.table + '.' + sel.field) ?? sel.field);
        series.unit = this.unitMap.get(sel.server + '.' + sel.dbase + '.' + sel.table + '.' + sel.field) ?? '';

        (this.$refs.lc as LineChartComponent).pushSeries(series);
    }

    private createTemplate(): void {
        if (!this.templates) {
            return;
        }
        (this.$refs.createtemplatemodal as CreateTemplateModal).openModal();
    }

    private async saveTemplates(): Promise<void> {
        await this.settingsAPI.update('ChartwizardTemplates', false, false, JSON.stringify(this.templates))
            .then(_ => { this.$toasted.info(this.$t('txtSaved').toString()); })
            .catch(e => handleError(this.$toasted, this.$i18n, e));
    }

    private onTemplateCreated(name: string): void {
        if (!this.templates) {
            return;
        }
        let data = (this.$refs.lc as LineChartComponent).chartData;
        let graphs = data.map(e => {
            return new ChartwizardTemplateGraph({
                server: e.server!, dbase: e.dbase!, table: e.table!, field: e.field!, color: e.color!,
                start: e.start!.getTime() - new Date().getTime(),
                end: e.end!.getTime() - new Date().getTime()
            });
        });
        let template = new ChartwizardTemplate({ name, graphs });
        this.templates.templates.push(template);
        this.saveTemplates();
        (this.$refs.createtemplatemodal as CreateTemplateModal).closeModal();
    }

    private async onTemplateSelected(template: ChartwizardTemplate): Promise<void> {
        this.resetChart();
        for (let graph of template.graphs) {
            await this.loadSelection(new Selection({
                ...graph,
                start: new Date(new Date().getTime() + graph.start),
                end: new Date(new Date().getTime() + graph.end)
            }), graph.color);
        }
    }

    private async onTemplateDeleted(template: ChartwizardTemplate): Promise<void> {
        let index = this.templates?.templates.findIndex(t => t === template);
        if (index === undefined) {
            return;
        }
        this.templates?.templates.splice(index, 1);
        this.saveTemplates();
    }

    //util
    private getRandomColor(): string {
        var letters = '0123456789ABCDEF';
        var color = '#';
        for (var i = 0; i < 6; i++) {
            color += letters[Math.floor(Math.random() * 16)];
        }
        return color;
    }

    private unique(data: string[]): string[] {
        let ret: string[] = [];
        data.forEach(e => {
            if (!ret.includes(e))
                ret.push(e);
        });
        return ret;
    }

    private uniqueSelectable(data: Selectable[]): Selectable[] {
        let ret: Selectable[] = [];
        data.forEach(e => {
            if (!ret.find(t => e.value === t.value))
                ret.push(e);
        });
        return ret;
    }

    //getters
    private get selection(): Selection {
        let sel = new Selection();
        sel.server = this.selectedServer?.value ?? '';
        sel.dbase = this.selectedTable?.value.split('.', 2)[0] ?? '';
        sel.table = this.selectedTable?.value.split('.', 2)[1] ?? '';
        sel.field = this.selectedField?.value ?? '';
        sel.start = this.from;
        sel.end = this.to;
        return sel;
    }

    private get servers(): Selectable[] {
        return this.unique(this.dbtables.map(t => t.server)).map(e => new Selectable(this.translationMap.get('server.' + e) ?? e, e));
    }

    private get tables(): Selectable[] {
        console.log(this.selectedServer);
        var server = this.selectedServer?.value;
        if (!server?.length) {
            return [];
        }
        return this.uniqueSelectable(this.dbtables
            .filter(t => t.server === server)
            .map(t => new Selectable(
                (this.translationMap.get('database.' + server + '.' + t.dbase) ?? t.dbase)
                + '.' +
                (this.translationMap.get('table.' + server + '.' + t.dbase + '.' + t.name) ?? t.name),
                t.dbase + '.' + t.name)));
    }

    private get fields(): Selectable[] {
        var server = this.selectedServer?.value;
        var dbase = this.selectedTable?.value.split('.', 2)[0];
        var table = this.selectedTable?.value.split('.', 2)[1];
        if (!server?.length || !dbase?.length || !table?.length) {
            return [];
        }
        var t = this.dbtables
            .find(t => t.server === server && t.dbase === dbase && t.name === table);
        return t ? t.fields.map(e => new Selectable(this.translationMap.get('field.' + server + '.' + dbase + '.' + table + '.' + e) ?? e, e)) : [];
    }

    private get translationMap(): Map<string, string> {
        let m = new Map<string, string>();
        this.translations.forEach(e => m.set(e.name, e.translation));
        return m;
    }

    private get unitMap(): Map<string, string> {
        let m = new Map<string, string>();
        this.units.forEach(e => m.set(e.name, e.unit));
        return m;
    }
}